import type { TFunction } from 'i18next';

import { titleCase } from '@cx-modules/utils/title-case';

export const supplantRegexp = /{{([^{{}}]*)}}/g;

const normaliseLabelKey = (str: string) =>
  str
    .replace(/[ /-]+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .replace(/_+/g, '_')
    .toUpperCase();

const getMarketLabel = ({ marketName, t }: { marketName: string; t: TFunction }) => {
  const marketNameKeyified = normaliseLabelKey(marketName);
  const labelKey = `CX_MARKET_TYPE_${marketNameKeyified}`;
  const labelValue = t(labelKey, { ns: 'label' });

  // if no label exists for the requested key, the key will be returned. Check and return the untranslated market name in this case.
  return labelValue === labelKey ? marketName : labelValue;
};

export const splitMatchup = (str: string) => {
  // Note: order matters — "at." should come before "at" so the longer one matches first
  const match = str.match(/(?<=\s)(at\.|vs\.?|@|v\.?|at)(?=\s)/i);
  if (!match) return null;

  const separator = match[1];
  const parts = str.split(new RegExp(`\\s${separator.replace('.', '\\.')}\\s`, 'i')).map(part => part.trim());

  return parts.length === 2 ? { parts, separator } : null;
};

export const supplant = (subjectString: string, data: Record<string, any>, translatorFunction: TFunction): string => {
  if (typeof subjectString !== 'string') {
    return '';
  }

  if (data == null || typeof data !== 'object') {
    return subjectString;
  }

  return RegExp(supplantRegexp.source).test(subjectString)
    ? subjectString.replace(supplantRegexp, (match, placeholder) => {
        const pathParts = placeholder.trim().split(/\./); // Split the variable into its dot notation parts.
        let currentValue = data; // This variable becomes our cursor that will traverse the object.

        // Loop over the steps in the dot notation path.
        for (let index = 0; index < pathParts.length; index += 1) {
          // If the key doesn't exist in the object do not process.
          // Mirrors how the function worked for bad values.
          if (currentValue[pathParts[index]] == null) {
            return match;
          }

          // Move the cursor up to the next step.
          currentValue = currentValue[pathParts[index]];
        }

        // If the data is a string or number, apply translator if available and return it,
        // otherwise do not process, return the value it was, i.e. {x}
        if (typeof currentValue === 'string') {
          const { SPORT = '' } = data;
          const sportNamespace = SPORT?.toLowerCase();
          switch (pathParts[0]) {
            case 'EVENT': {
              if (!sportNamespace || !translatorFunction) {
                return currentValue;
              }
              const result = translatorFunction(currentValue, {
                ns: sportNamespace,
              });

              return result;
            }
            case 'MARKET':
              return translatorFunction
                ? getMarketLabel({
                    marketName: currentValue,
                    t: translatorFunction,
                  })
                : currentValue;
            case 'SPORT':
              return translatorFunction
                ? translatorFunction(titleCase(currentValue), {
                    ns: 'label',
                  })
                : currentValue;
            case 'SUBEVENT': {
              const result = splitMatchup(currentValue);
              if (result) {
                const [teamA, teamB] = result.parts.map(part => {
                  return translatorFunction
                    ? translatorFunction(part ? titleCase(part) : '', {
                        ns: sportNamespace,
                      })
                    : part;
                });

                const reconstructed = `${teamA} ${result.separator} ${teamB}`;
                return reconstructed;
              }

              return currentValue;
            }
            default: {
              return currentValue;
            }
          }
        }
        return match;
      })
    : subjectString;
};

/**
 * Transforms a string of placeholders key-value pairs into an object.
 *
 * @param {String} [placeholdersString=''] The placeholders in the following format: `KEY_1=VALUE_1|KEY_2=VALUE_2`.
 * @returns {Object} Returns an object that represents the placeholders as an object.
 */
export const placeholdersEntries = (placeholdersString = '') => {
  if (typeof placeholdersString !== 'string' || !placeholdersString.trim()) {
    return {};
  }

  const separator = '|'; // Just a convention between devs and content team.
  const pairs = placeholdersString.split(separator).map(pair => {
    return pair
      .trim()
      .split('=')
      .map(part => part.trim());
  });

  return Object.fromEntries(pairs);
};

/**
 * Replaces placeholders from `subjectString` with the appropriate value of `placeholdersString`.
 *
 * @param {String} subjectString The subject string to apply replacements.
 * @param {String} placeholdersString The placeholders in the following format: `KEY_1=VALUE_1|KEY_2=VALUE_2`.
 * @param {String} [fallbackString=''] A fallback string to return if a placeholder value does not exist in the provided placeholders string.
 * @returns {String} Returns the processed string.
 */
export const applyPlaceholders = ({
  fallbackString = '',
  placeholdersString,
  subjectString,
  translatorFunction,
}: {
  fallbackString: string;
  placeholdersString?: string;
  subjectString: string;
  translatorFunction: TFunction;
}) => {
  const placeholdersObj = placeholdersEntries(placeholdersString);
  const resultString = supplant(subjectString, placeholdersObj, translatorFunction);

  // Check the final string and if there are occurences of unreplaced placeholders
  // return a fallback string if exists, otherwise just return the result string as is.
  if (RegExp(supplantRegexp.source).test(resultString)) {
    return fallbackString || resultString;
  } else {
    return resultString;
  }
};

export const getValueFromPlaceholder = ({
  placeholders,
  template = '',
  translatorFunction,
  value = '',
}: {
  placeholders?: string;
  template?: string;
  translatorFunction: TFunction<any>;
  value?: string;
}) => {
  if (!template || !placeholders) {
    return value;
  }
  return value
    ? applyPlaceholders({
        fallbackString: value.trim(),
        placeholdersString: placeholders,
        subjectString: template.trim(),
        translatorFunction,
      })
    : value;
};
